<template>
    <layout-main>
        <head-title value="End-User License Agreement" />

        <layout-content>
            <content-header>
                <top-nav>
                    <top-nav-back />
                    <top-nav-title>
                        EULA
                    </top-nav-title>
                </top-nav>
            </content-header>

            <content-body>
                <div class="max-w-4xl mx-auto px-4 pb-8 pt-4">
                    <div class="mb-4">
                        <h1 class="font-semibold mb-1">End-User License Agreement (EULA)</h1>
                        <div class="text-sm">
                            <span class="font-semibold">Effective Date:</span>
                            January 14, 2025 (Applicable from April 17, 2024)
                        </div>
                        <div class="mt-2">
                            This End-User License Agreement ("Agreement") is a legal agreement between you ("User") and Corperland Ltd ("Company") governing your use of the Corperland platform, including any related websites, applications, or services (collectively, the "Platform"). By accessing or using the Platform, you agree to be bound by the terms of this Agreement. If you do not agree, do not use the Platform.
                        </div>
                    </div>
                    <div>
                        <div class="flex flex-col gap-8">
                            <div>
                                <h2 class="text-xl font-bold mb-4">1. License Grant</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        The Company grants you a non-exclusive, non-transferable, revocable license to access and use the Platform for personal purposes or commercial purposes permitted by the Platform’s functionalities (e.g., transactions conducted through the marketplace). All commercial activities must comply with this Agreement and applicable laws.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">2. Restrictions on Use</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        You agree not to:
                                    </p>
                                    <ol class="flex flex-col gap-2 list-decimal pl-8">
                                        <li>Copy, modify, distribute, or create derivative works from the Platform.</li>
                                        <li>Reverse engineer, decompile, or attempt to extract the source code of the Platform.</li>
                                        <li>Use the Platform for unlawful or unauthorized purposes.</li>
                                        <li>Interfere with or disrupt the integrity or performance of the Platform.</li>
                                        <li>Transfer, sublicense, or assign your rights under this Agreement.</li>
                                    </ol>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">3. User Content</h2>
                                <div class="flex flex-col gap-4">
                                    <ol class="flex flex-col gap-2 list-decimal pl-8">
                                        <li><span class="font-semibold">Ownership:</span> You retain ownership of any content you upload, post, or otherwise make available through the Platform ("User Content").</li>
                                        <li><span class="font-semibold">License to Company:</span> By providing User Content, you grant the Company a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, and display the content for purposes of operating and improving the Platform.</li>
                                        <li><span class="font-semibold">Responsibility:</span> You are solely responsible for your User Content and must ensure it does not violate applicable laws, infringe the rights of third parties, or contain objectionable or abusive material.</li>
                                        <li><span class="font-semibold">Zero Tolerance:</span> The Company has zero tolerance for objectionable content or abusive behavior. Users who violate this provision may have their accounts suspended or terminated without notice.</li>
                                    </ol>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">4. Privacy Policy</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        Your use of the Platform is also governed by our Privacy Policy, which explains how we collect, use, and protect your information. By using the Platform, you consent to the practices described in the Privacy Policy.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">5. Updates and Modifications</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        The Company may update the Platform or this Agreement at any time. Continued use of the Platform after such updates constitutes your acceptance of the updated terms. If you do not agree to the updated terms, you must stop using the Platform.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">6. Termination</h2>
                                <div class="flex flex-col gap-4">
                                    <ol class="flex flex-col gap-2 list-decimal pl-8">
                                        <li>The Company may suspend or terminate your access to the Platform at any time without notice if you violate this Agreement.</li>
                                        <li>You may terminate this Agreement by ceasing all use of the Platform.</li>
                                        <li>Upon termination, all rights granted to you under this Agreement will immediately cease.</li>
                                    </ol>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">7. Disclaimer of Warranties</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        The Platform is provided "as is" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. The Company does not guarantee uninterrupted or error-free operation of the Platform.
                                    </p>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">8. Limitation of Liability</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        To the maximum extent permitted by law, the Company shall not be liable for any indirect, incidental, consequential, or punitive damages, including loss of data, revenue, or profits, arising out of your use or inability to use the Platform
                                    </p>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">9. Governing Law and Dispute Resolution</h2>
                                <div class="flex flex-col gap-4">
                                    <ol class="flex flex-col gap-2 list-decimal pl-8">
                                        <li><span class="font-semibold">Governing Law:</span> This Agreement is governed by the laws of the Federal Republic of Nigeria, without regard to conflict of laws principles.</li>
                                        <li><span class="font-semibold">Dispute Resolution:</span> Any disputes arising from or related to this Agreement shall be resolved through binding arbitration in accordance with the rules of the Lagos Court of Arbitration (LCA), and judgment on the award rendered by the arbitrator may be entered in any court having jurisdiction.</li>
                                    </ol>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">10. Miscellaneous</h2>
                                <div class="flex flex-col gap-4">
                                    <ol class="flex flex-col gap-2 list-decimal pl-8">
                                        <li><span class="font-semibold">Entire Agreement:</span> This Agreement constitutes the entire agreement between you and the Company regarding the use of the Platform.</li>
                                        <li><span class="font-semibold">Severability:</span> If any provision of this Agreement is found to be unenforceable, the remaining provisions will remain in full force and effect.</li>
                                    </ol>
                                </div>
                            </div>
                            <div>
                                <h2 class="text-xl font-bold mb-4">Contact Us</h2>
                                <div class="flex flex-col gap-4">
                                    <p>
                                        If you have any questions or concerns about this Agreement, please contact us at:
                                    </p>
                                    <p>Email: <a href="mailto:hello@corperland.ng" class="text-info">hello@corperland.ng</a></p>
                                    <p>By using the Platform, you acknowledge that you have read, understood, and agreed to be bound by this End-User License Agreement.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </content-body>
        </layout-content>
    </layout-main>
</template>
